import React, { useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useRequest } from 'ahooks'

import { ActionButtonIcon } from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import {
  makeConfirmEmailURL,
  enroll2faSmsPath,
  sendResetPassword,
  login2faPath,
  googleOauthPath,
  checkIsEmailEnterpriseSSOPath,
} from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { isNotEmpty, isValidEmail } from '$extensionSrc/utils/stringValidator'
import { authFetch } from '$serverSrc/utils/fetch'

import ContinueWithGoogle from './ContinueWithGoogle'
import FormSubmitButton from './FormSubmitButton'
import FormTextDescription from './FormTextDescription'
import FormTextInput from './FormTextInput'
import LoginSignupLink from './LoginSignupLink'
import { unauthClient } from '../apolloClientUnauth'
import { UserExceptionType } from '../consts/enums'
import { loginMutation } from '../data/queryUnauth'

import './LoginForm.scss'

export function handleLoginResponse(
  loginError: string,
  nextUrl: string,
  email: string,
  enterpriseSsoLoginUrl: string,
) {
  if (!loginError) {
    const redirectUrl = new URL(
      decodeURIComponent(nextUrl),
      window.location.href,
    )
    window.location.assign(redirectUrl.href)
  } else if (
    loginError === UserExceptionType.USER_EMAIL_NOT_CONFIRMED_EXCEPTION
  ) {
    window.location.assign(makeConfirmEmailURL(email))
  } else if (loginError === UserExceptionType.USER_2FA_NOT_SET_UP_EXCEPTION) {
    window.location.assign(enroll2faSmsPath)
  } else if (loginError === UserExceptionType.USER_2FA_REQUIRED_EXCEPTION) {
    window.location.assign(login2faPath)
  } else if (
    loginError ===
    UserExceptionType.USER_HAS_ENTERPRISE_SSO_EMAIL_DOMAIN_EXCEPTION
  ) {
    if (enterpriseSsoLoginUrl) {
      window.location.assign(enterpriseSsoLoginUrl)
    }
  }
}

export default function LoginForm({
  initialError,
  nextUrl,
  urlParams,
}: {
  initialError: string
  nextUrl: string
  urlParams: { link?: string }
}) {
  const defaultValues = { email: '', password: '' }
  const requiredValidations = {
    email: (x: string) => isValidEmail(x),
    password: (x: string) => isNotEmpty(x),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const [submitLoginRequest, { data: loginResponseData }] = useMutation(
    loginMutation,
    { client: unauthClient },
  )
  const loginStatusCode = loginResponseData?.login?.statusCode
  const loginError = loginResponseData?.login?.error

  // Only used if we detect enterprise SSO
  const enterpriseSsoLoginUrl = loginResponseData?.login?.enterpriseSsoLoginUrl

  if (loginStatusCode === 200) {
    handleLoginResponse(
      loginError,
      nextUrl,
      values.email,
      enterpriseSsoLoginUrl,
    )
  }

  const [isEnterpriseSSO, setIsEnterpriseSSO] = useState<boolean>(false)

  const { run: getIsEmailEnterpriseSSO } = useRequest(
    () => {
      const formData = new FormData()
      formData.append('email', values.email)

      return authFetch(
        new Request(checkIsEmailEnterpriseSSOPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      debounceWait: 300,
      onSuccess: (response: Response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.is_enterprise_sso) {
              setIsEnterpriseSSO(true)
            } else {
              setIsEnterpriseSSO(false)
            }
          } else {
            setIsEnterpriseSSO(false)
          }
        })
      },
    },
  )

  const emailValidationMet = useMemo(
    () => getRequiredValidationMetByField('email'),
    [getRequiredValidationMetByField],
  )

  useEffect(() => {
    if (emailValidationMet) {
      getIsEmailEnterpriseSSO()
    }
  }, [values.email, emailValidationMet, getIsEmailEnterpriseSSO])

  const submitForm: React.FormEventHandler = (event) => {
    event.preventDefault()
    submitLoginRequest({
      variables: {
        email: values.email,
        password: values.password,
      },
    })
  }

  const redirectUrl: string = useMemo(() => {
    const usp = new URLSearchParams()
    if (urlParams) {
      Object.entries(urlParams).forEach(([key, value]) => {
        usp.set(key, value)
      })
    }
    usp.set('next', nextUrl)
    usp.set('action', 'login')
    return `${googleOauthPath}?${usp.toString()}`
  }, [urlParams, nextUrl])

  return (
    <form className="LoginForm" onSubmit={submitForm}>
      <div className="form-header">
        <div className="form-title bold heading1">Log into your account</div>
      </div>
      <ContinueWithGoogle redirectUrl={redirectUrl} />
      <div className="form-option-divider">
        <div className="divider" />
        <div className="or-text body2">or</div>
        <div className="divider" />
      </div>
      <div className="form-content">
        <FormTextInput
          placeholderText="Email address"
          name="email"
          isSensitive={false}
          value={values.email}
          handleChange={(value) => getFieldChangeHandler('email')(value)}
          requirements={[
            {
              text: 'Enter a valid email address',
              isFulfilled: getRequiredValidationMetByField('email'),
            },
          ]}
        />
        {!isEnterpriseSSO && (
          <FormTextInput
            placeholderText="Password"
            name="password"
            isSensitive
            value={values.password}
            handleChange={(value) => getFieldChangeHandler('password')(value)}
          />
        )}
      </div>
      {isEnterpriseSSO ? (
        <FormSubmitButton
          isValid={isEnterpriseSSO}
          iconName={ActionButtonIcon.GENERAL_OAUTH}
        >
          Continue with your employer
        </FormSubmitButton>
      ) : (
        <>
          <FormSubmitButton isValid={isValid}>Log In</FormSubmitButton>
          <FormTextDescription align="center">
            <a href={sendResetPassword}>I forgot my password</a>
          </FormTextDescription>
        </>
      )}
      {loginStatusCode !== 200 && loginError && (
        <Toast
          text={
            loginError ===
            UserExceptionType.USER_EMAIL_PASSWORD_ATTEMPTS_THROTTLED
              ? 'Too many failed attempts. Try again after 15 minutes.'
              : 'Credentials invalid. Try another method or reset your password.'
          }
          color="red"
          noAutoDismiss
          showCloseIcon
        />
      )}
      {initialError && (
        <Toast text={initialError} color="red" noAutoDismiss showCloseIcon />
      )}
      <LoginSignupLink />
    </form>
  )
}
